// AttentionChip — the marketplace "attention banner" corner chip
// (operator-approved design 2026-08-16). One optional operator-set tag per
// product: a small uppercase chip whose background is the operator's chosen
// color and whose text color is computed for contrast (ITU-R BT.601
// luminance). NOT a diagonal ribbon; it is pinned by its consumers
// (ProductCard tiles) or rendered inline (PDP tag row).
//
// Inline styles only — no Tailwind. No imports from portal/.
//
// The preset constants are NOT defined here: their canonical home is
// embed-shared/attention-presets.ts (importable by BOTH the portal and the
// embed); this module re-exports them so embed consumers have one import
// site for everything attention-banner-related.

import { useEmbedTokens } from "../../embed-shared/primitives";
import {
  ATTENTION_COLOR_PATTERN,
  ATTENTION_PRESETS,
  ATTENTION_LABEL_MAX,
  isValidAttentionColor,
} from "../../embed-shared/attention-presets";

export { ATTENTION_PRESETS, ATTENTION_LABEL_MAX };

/** Near-black text for pale chip backgrounds. */
export const ATTENTION_TEXT_DARK = "#111111";
/** White text for dark chip backgrounds. */
export const ATTENTION_TEXT_LIGHT = "#FFFFFF";
/** Normalized-luminance threshold: below this the background is "dark" → white text. */
export const ATTENTION_LUMINANCE_THRESHOLD = 0.55;

/**
 * Text color to pair with an attention chip background, per ITU-R BT.601:
 * (0.299R + 0.587G + 0.114B) / 255 — white below the threshold, near-black
 * at/above it. Invalid or absent colors return WHITE: the chip pairs that
 * return with its theme-accent fallback background, whose luminance is
 * below the threshold in both themes (light #1D59FF ≈ 0.35, dark #5B8DEF
 * ≈ 0.54).
 */
export function attentionTextColor(color: string | null | undefined): string {
  const hex = typeof color === "string" ? color.trim() : "";
  if (!ATTENTION_COLOR_PATTERN.test(hex)) return ATTENTION_TEXT_LIGHT;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < ATTENTION_LUMINANCE_THRESHOLD
    ? ATTENTION_TEXT_LIGHT
    : ATTENTION_TEXT_DARK;
}

export interface AttentionChipProps {
  /** The tag text, rendered as typed (CSS uppercases it visually — screen
   *  readers announce the operator's own casing). */
  label: string;
  /** `#RRGGBB`; invalid/absent falls back to the theme's accent token. */
  color?: string | null;
}

/**
 * The chip carries the tag, so its text is meaningful and readable — never
 * aria-hidden.
 */
export function AttentionChip({ label, color }: AttentionChipProps) {
  const t = useEmbedTokens();
  const valid = isValidAttentionColor(color);
  const background = valid ? (color as string).trim() : t.colors.primary;
  const textColor = valid ? attentionTextColor(color) : ATTENTION_TEXT_LIGHT;
  return (
    <span
      data-testid="attention-chip"
      style={{
        display: "inline-block",
        background,
        color: textColor,
        borderRadius: t.borderRadius,
        padding: `${t.spacing.xs}px ${t.spacing.sm}px`,
        fontSize: t.fontSize.xs,
        fontWeight: 700,
        lineHeight: 1,
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}
